import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "@/lib/apiClient";

type LeadRecordContactSnapshot = {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  title?: string | null;
  email?: string | null;
  linkedinUrl?: string | null;
  phone?: string | null;
  contactId?: string | null;
};

type LeadRecord = {
  id: string;
  leadName: string;
  source: string;
  sourceDetail?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  companyWebsite?: string | null;
  companyIndustry?: string | null;
  contacts: LeadRecordContactSnapshot[];
  status: string;
};

type LeadAccount = {
  id: string;
  name: string;
  domain?: string | null;
  website?: string | null;
  industry?: string | null;
};

type ExistingContact = {
  id: string;
  accountId: string;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
};

type Conflict = {
  index: number;
  existingContact: ExistingContact;
  incomingContact: LeadRecordContactSnapshot;
};

type CompanyConflict = {
  existingAccount: LeadAccount;
  reason: "name_match" | "domain_match" | "domain_and_name_match";
  incomingCompany: {
    name: string;
    domain?: string | null;
    website?: string | null;
    industry?: string | null;
  };
};

type CompanyFieldSelections = Partial<
  Record<"name" | "domain" | "website" | "industry", "incoming" | "existing">
>;

type ContactDecision = {
  index: number;
  action?: "create_new" | "update" | "overwrite" | "skip";
  fieldSelections?: Partial<
    Record<"firstName" | "lastName" | "title" | "email" | "phone" | "linkedinUrl", "incoming" | "existing">
  >;
};

function normalizeCompareValue(value: string | null | undefined): string {
  return (value || "").trim().toLowerCase();
}

function hasFieldDifference(
  currentValue: string | null | undefined,
  convertedValue: string | null | undefined,
): boolean {
  return normalizeCompareValue(currentValue) !== normalizeCompareValue(convertedValue);
}

export function CrmLeadConvertPage() {
  const params = useParams() as { leadId?: string };
  const navigate = useNavigate();
  const leadId = params?.leadId;

  const [lead, setLead] = useState<LeadRecord | null>(null);
  const [accounts, setAccounts] = useState<LeadAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [companyConflict, setCompanyConflict] = useState<CompanyConflict | null>(null);
  const [companyDuplicateAction, setCompanyDuplicateAction] = useState<"create_new" | "update" | "overwrite">(
    "update",
  );
  const [companyFieldSelections, setCompanyFieldSelections] = useState<CompanyFieldSelections>({});

  const [companyMode, setCompanyMode] = useState<"existing" | "new" | "skip">("new");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyDomain, setNewCompanyDomain] = useState("");
  const [newCompanyWebsite, setNewCompanyWebsite] = useState("");
  const [newCompanyIndustry, setNewCompanyIndustry] = useState("");
  const [contactDecisions, setContactDecisions] = useState<ContactDecision[]>([]);

  useEffect(() => {
    if (!leadId) return;

    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [leadRes, accountsRes] = await Promise.all([
          apiGet<LeadRecord>(`/api/crm/leads/${encodeURIComponent(leadId)}`),
          apiGet<{ accounts?: LeadAccount[] }>("/api/crm/accounts?limit=200"),
        ]);

        if (!leadRes.ok || !leadRes.data) {
          throw new Error(leadRes.error || "Unable to load lead for conversion.");
        }
        if (!accountsRes.ok) {
          throw new Error(accountsRes.error || "Unable to load accounts.");
        }

        const leadData = leadRes.data;
        const accountsData = accountsRes.data;

        if (!active) return;
        setLead(leadData);
        setAccounts(accountsData?.accounts || []);
        setNewCompanyName(leadData.companyName || "");
        setNewCompanyDomain(leadData.companyDomain || "");
        setNewCompanyWebsite(leadData.companyWebsite || "");
        setNewCompanyIndustry(leadData.companyIndustry || "");
        setContactDecisions(
          leadData.contacts.map((_, index) => ({
            index,
            action: undefined,
            fieldSelections: {},
          })),
        );
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Unable to load conversion data.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [leadId]);

  const conflictMap = useMemo(
    () =>
      new Map<number, Conflict>(
        conflicts.map((conflict) => [conflict.index, conflict]),
      ),
    [conflicts],
  );

  const updateDecision = (index: number, updates: Partial<ContactDecision>) => {
    setContactDecisions((current) =>
      current.map((decision) => (decision.index === index ? { ...decision, ...updates } : decision)),
    );
  };

  const submitConversion = async () => {
    if (!lead) return;
    if (companyMode === "existing" && !selectedAccountId) {
      setError("Select an account for conversion.");
      return;
    }
    if (companyMode === "new" && !newCompanyName.trim()) {
      setError("Enter a company name to create a new account.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const resolvedContactDecisions: ContactDecision[] = contactDecisions.map((decision) => {
        if (decision.action === "skip") {
          return decision;
        }

        return {
          ...decision,
          // If the user has not explicitly chosen a duplicate action yet, default to update.
          action: decision.action || "update",
        };
      });

      const payload = {
        company:
          companyMode === "skip"
            ? { mode: "skip" as const }
            : companyMode === "existing"
            ? { mode: "existing" as const, accountId: selectedAccountId }
            : {
                mode: "new" as const,
                name: newCompanyName.trim(),
                domain: newCompanyDomain.trim() || null,
                website: newCompanyWebsite.trim() || null,
                industry: newCompanyIndustry.trim() || null,
                duplicateAction: companyConflict ? companyDuplicateAction : undefined,
                duplicateAccountId: companyConflict?.existingAccount.id || null,
                duplicateFieldSelections:
                  companyConflict && companyDuplicateAction === "update" ? companyFieldSelections : undefined,
              },
        contacts: resolvedContactDecisions,
      };

      const res = await apiPost<{
        error?: string;
        lead: LeadRecord | null;
        account: LeadAccount | null;
        conflicts?: Conflict[];
        companyConflict?: CompanyConflict | null;
      }>(`/api/crm/leads/${encodeURIComponent(lead.id)}/convert`, payload);
      const data = res.data;

      if (!res.ok || !data) {
        throw new Error(res.error || data?.error || "Unable to convert lead.");
      }

      const nextConflicts = data.conflicts || [];
      setCompanyConflict(companyMode === "skip" ? null : data.companyConflict || null);
      setConflicts(nextConflicts);
      if ((companyMode !== "skip" && data.companyConflict) || nextConflicts.length > 0) {
        setContactDecisions((current) =>
          current.map((decision) =>
            nextConflicts.some((conflict) => conflict.index === decision.index)
              ? { ...decision, action: decision.action || "update" }
              : decision,
          ),
        );
        setError(
          companyMode === "skip"
            ? "Duplicate contacts were found. Choose Create New, Update, or Overwrite, then confirm again."
            : "Duplicate company/contact records were found. Choose Create New, Update, or Overwrite, then confirm again.",
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      setSuccess("Lead converted successfully.");
      const convertedLeadId = data.lead?.id || lead.id;
      const accountId = data.account?.id;
      const destination = accountId
        ? `/crm/leads/${convertedLeadId}/converted?accountId=${encodeURIComponent(accountId)}`
        : `/crm/leads/${convertedLeadId}/converted`;
      navigate(destination);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to convert lead.");
    } finally {
      setSaving(false);
    }
  };

  const displayName = (contact: LeadRecordContactSnapshot) =>
    contact.fullName ||
    [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
    "Unknown Contact";

  if (loading) {
    return <div className="text-sm text-gray-600 dark:text-gray-300">Loading conversion details...</div>;
  }

  if (!lead) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600 dark:text-red-300">Lead not found.</p>
        <Link to="/crm/leads" className="text-sm text-indigo-600 hover:underline dark:text-indigo-300">
          Back to Leads
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Convert Lead</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Confirm company (optional) and contacts before converting{" "}
            <span className="font-medium">{lead.leadName}</span>.
          </p>
        </div>
        <Link
          to="/crm/leads"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Cancel
        </Link>
      </div>

      {error && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-900/20 dark:text-emerald-200">
          {success}
        </div>
      )}

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-white/10">
        <h2 className="text-lg font-semibold">1) Company Confirmation</h2>
        <div className="flex flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={companyMode === "new"}
              onChange={() => setCompanyMode("new")}
            />
            Create or use a new company
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={companyMode === "existing"}
              onChange={() => setCompanyMode("existing")}
            />
            Use an existing account
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={companyMode === "skip"}
              onChange={() => {
                setCompanyMode("skip");
                setCompanyConflict(null);
              }}
            />
            Skip company (contacts only)
          </label>
        </div>

        {companyMode === "existing" ? (
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Select Account</span>
            <select
              value={selectedAccountId}
              onChange={(event) => setSelectedAccountId(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
            >
              <option value="">Choose an account</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({account.id})
                </option>
              ))}
            </select>
          </label>
        ) : companyMode === "new" ? (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium">Company Name</span>
              <input
                value={newCompanyName}
                onChange={(event) => setNewCompanyName(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">Domain</span>
              <input
                value={newCompanyDomain}
                onChange={(event) => setNewCompanyDomain(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">Website</span>
              <input
                value={newCompanyWebsite}
                onChange={(event) => setNewCompanyWebsite(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">Industry</span>
              <input
                value={newCompanyIndustry}
                onChange={(event) => setNewCompanyIndustry(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              />
            </label>
          </div>
        ) : (
          <p className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-700/50 dark:bg-blue-900/20 dark:text-blue-200">
            Company conversion is skipped. This lead can be converted without creating or linking an account first.
          </p>
        )}

        {companyMode !== "skip" && companyConflict && (
          <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/50 dark:bg-amber-900/20">
            <p className="font-medium">
              Duplicate company found: <span className="font-semibold">{companyConflict.existingAccount.name}</span>{" "}
              ({companyConflict.reason.replace(/_/g, " ")}).
            </p>
            <label className="block space-y-1">
              <span className="font-medium">Duplicate Resolution</span>
              <select
                value={companyDuplicateAction}
                onChange={(event) =>
                  setCompanyDuplicateAction(
                    event.target.value as "create_new" | "update" | "overwrite",
                  )
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
              >
                <option value="create_new">Create New</option>
                <option value="update">Update Existing</option>
                <option value="overwrite">Overwrite Existing</option>
              </select>
            </label>
            {companyDuplicateAction === "update" && (
              <div className="grid gap-2">
                {(() => {
                  const differingCompanyFields = (["name", "domain", "website", "industry"] as const).filter(
                    (field) =>
                      hasFieldDifference(
                        companyConflict.existingAccount[field],
                        companyConflict.incomingCompany[field],
                      ),
                  );

                  if (differingCompanyFields.length === 0) {
                    return (
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        No field differences found. `Update Existing` will keep current values as-is.
                      </p>
                    );
                  }

                  return differingCompanyFields.map((field) => (
                    <div
                      key={field}
                      className="rounded-md border border-gray-200 bg-white p-2 dark:border-white/10 dark:bg-black/20"
                    >
                      <p className="mb-1 text-xs font-semibold capitalize">{field}</p>
                      <div className="grid gap-2 md:grid-cols-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="radio"
                            name={`company-merge-${field}`}
                            checked={(companyFieldSelections[field] || "existing") === "existing"}
                            onChange={() =>
                              setCompanyFieldSelections((current) => ({ ...current, [field]: "existing" }))
                            }
                          />
                          Current: {String(companyConflict.existingAccount[field] || "Empty")}
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="radio"
                            name={`company-merge-${field}`}
                            checked={(companyFieldSelections[field] || "existing") === "incoming"}
                            onChange={() =>
                              setCompanyFieldSelections((current) => ({ ...current, [field]: "incoming" }))
                            }
                          />
                          Converted: {String(companyConflict.incomingCompany[field] || "Empty")}
                        </label>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-white/10">
        <h2 className="text-lg font-semibold">2) Contact Confirmation</h2>
        {lead.contacts.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No contacts exist on this lead. Conversion will create or link only the company.
          </p>
        )}
        <div className="space-y-4">
          {lead.contacts.map((contact, index) => {
            const decision = contactDecisions.find((item) => item.index === index);
            const conflict = conflictMap.get(index);
            return (
              <div key={`contact-${index}`} className="rounded-md border border-gray-200 p-3 dark:border-white/10">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">{displayName(contact)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {contact.email || "No email"} · {contact.linkedinUrl || "No LinkedIn"}
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <span>Convert contact</span>
                    <input
                      type="checkbox"
                      checked={decision?.action !== "skip"}
                      onChange={(event) =>
                        updateDecision(index, { action: event.target.checked ? undefined : "skip" })
                      }
                    />
                  </label>
                </div>

                {conflict && (
                  <div className="mt-3 space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/50 dark:bg-amber-900/20">
                    <p className="font-medium">
                      Conflict: matching contact already exists on account {conflict.existingContact.accountId}.
                    </p>
                    <label className="block space-y-1">
                      <span className="font-medium">Duplicate Resolution</span>
                      <select
                        value={decision?.action || "update"}
                        onChange={(event) =>
                          updateDecision(index, {
                            action: event.target.value as ContactDecision["action"],
                          })
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                      >
                        <option value="create_new">Create New</option>
                        <option value="update">Update Existing</option>
                        <option value="overwrite">Overwrite Existing</option>
                      </select>
                    </label>
                    {(decision?.action || "update") === "update" && (
                      <div className="grid gap-2">
                        {(() => {
                          const differingContactFields = (
                            ["firstName", "lastName", "title", "email", "phone", "linkedinUrl"] as const
                          ).filter((field) =>
                            hasFieldDifference(conflict.existingContact[field], contact[field]),
                          );

                          if (differingContactFields.length === 0) {
                            return (
                              <p className="text-xs text-gray-600 dark:text-gray-300">
                                No field differences found. `Update Existing` will keep current values as-is.
                              </p>
                            );
                          }

                          return differingContactFields.map((field) => (
                            <div
                              key={field}
                              className="rounded-md border border-gray-200 bg-white p-2 dark:border-white/10 dark:bg-black/20"
                            >
                              <p className="mb-1 text-xs font-semibold capitalize">{field}</p>
                              <div className="grid gap-2 md:grid-cols-2">
                                <label className="flex items-center gap-2 text-xs">
                                  <input
                                    type="radio"
                                    name={`contact-merge-${index}-${field}`}
                                    checked={(decision?.fieldSelections?.[field] || "existing") === "existing"}
                                    onChange={() =>
                                      updateDecision(index, {
                                        fieldSelections: {
                                          ...(decision?.fieldSelections || {}),
                                          [field]: "existing",
                                        },
                                      })
                                    }
                                  />
                                  Current: {String(conflict.existingContact[field] || "Empty")}
                                </label>
                                <label className="flex items-center gap-2 text-xs">
                                  <input
                                    type="radio"
                                    name={`contact-merge-${index}-${field}`}
                                    checked={(decision?.fieldSelections?.[field] || "existing") === "incoming"}
                                    onChange={() =>
                                      updateDecision(index, {
                                        fieldSelections: {
                                          ...(decision?.fieldSelections || {}),
                                          [field]: "incoming",
                                        },
                                      })
                                    }
                                  />
                                  Converted: {String(contact[field] || "Empty")}
                                </label>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={submitConversion}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Converting..." : "Confirm and Convert"}
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          If duplicates are found, choose Create New, Update, or Overwrite and confirm again.
        </p>
      </div>
    </div>
  );
}
